import { apiService } from './api';
import { Item } from '@/types';

export interface LabelTemplate {
  id: string;
  name: string;
  width: number; // mm
  height: number; // mm
  description: string;
  supportsLogo: boolean;
  supportsQR: boolean;
  supportsText: boolean;
}

export interface LabelPrintRequest {
  templateId: string;
  includeQR?: boolean;
  includeLogo?: boolean;
  customText?: string;
  copies?: number;
  printerName?: string;
}

export interface BatchPrintRequest {
  items: Array<{
    itemId: string;
    templateId: string;
    includeQR?: boolean;
    includeLogo?: boolean;
    customText?: string;
    copies?: number;
  }>;
  printerName?: string;
}

export interface PrintResult {
  success: boolean;
  message: string;
}

export interface BatchPrintResult {
  success: boolean;
  message: string;
  results: Array<{
    itemId: string;
    itemName?: string;
    success: boolean;
    message: string;
  }>;
}

class LabelService {
  /**
   * Holt alle verfügbaren Label-Templates
   */
  async getTemplates(): Promise<LabelTemplate[]> {
    console.log('Fetching label templates...');
    const response = await apiService.get('/labels/templates');
    console.log('Label templates response:', response);
    return response;
  }

  /**
   * Holt verfügbare Drucker
   */
  async getPrinters(): Promise<string[]> {
    const response = await apiService.get('/labels/printers');
    return response;
  }

  /**
   * Generiert eine Label-Vorschau für ein Item
   */
  async generatePreview(itemId: string, request: LabelPrintRequest): Promise<string> {
    const response = await apiService.post(`/labels/preview/${itemId}`, request, {
      responseType: 'text'
    });
    return response;
  }

  /**
   * Generiert eine Label-Vorschau mit Dimensionsinformationen
   */
  async generatePreviewWithDimensions(itemId: string, request: LabelPrintRequest): Promise<{
    svg: string;
    dimensions: {
      widthPx: number;
      heightPx: number;
      widthMm: number;
      heightMm: number;
    } | null;
    originalDimensions: {
      widthPx: number;
      heightPx: number;
      widthMm: number;
      heightMm: number;
    } | null;
  }> {
    try {
      const response = await fetch(`/api/labels/preview/${itemId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const svg = await response.text();
      
      // Extrahiere Dimensionen aus Response Headers (für Anzeige)
      const widthPx = response.headers.get('X-Label-Width-Px');
      const heightPx = response.headers.get('X-Label-Height-Px');
      const widthMm = response.headers.get('X-Label-Width-Mm');
      const heightMm = response.headers.get('X-Label-Height-Mm');

      // Extrahiere ursprüngliche Dimensionen (für PNG-Generierung)
      const originalWidthPx = response.headers.get('X-Label-Original-Width-Px');
      const originalHeightPx = response.headers.get('X-Label-Original-Height-Px');
      const originalWidthMm = response.headers.get('X-Label-Original-Width-Mm');
      const originalHeightMm = response.headers.get('X-Label-Original-Height-Mm');

      let dimensions = null;
      if (widthPx && heightPx && widthMm && heightMm) {
        dimensions = {
          widthPx: parseFloat(widthPx),
          heightPx: parseFloat(heightPx),
          widthMm: parseFloat(widthMm),
          heightMm: parseFloat(heightMm)
        };
        console.log('Label-Dimensionen (Anzeige) vom Backend erhalten:', dimensions);
      }

      let originalDimensions = null;
      if (originalWidthPx && originalHeightPx && originalWidthMm && originalHeightMm) {
        originalDimensions = {
          widthPx: parseFloat(originalWidthPx),
          heightPx: parseFloat(originalHeightPx),
          widthMm: parseFloat(originalWidthMm),
          heightMm: parseFloat(originalHeightMm)
        };
        console.log('Label-Dimensionen (Original für PNG) vom Backend erhalten:', originalDimensions);
      }

      return { svg, dimensions, originalDimensions };
    } catch (error) {
      console.error('Fehler beim Laden der Vorschau mit Dimensionen:', error);
      // Fallback zur normalen Methode
      const svg = await this.generatePreview(itemId, request);
      return { svg, dimensions: null, originalDimensions: null };
    }
  }

  /**
   * Druckt ein Label für ein Item
   */
  async printLabel(itemId: string, request: LabelPrintRequest): Promise<PrintResult> {
    const response = await apiService.post(`/labels/print/${itemId}`, request);
    return response;
  }

  /**
   * Druckt Labels für mehrere Items
   */
  async batchPrint(request: BatchPrintRequest): Promise<BatchPrintResult> {
    const response = await apiService.post('/labels/batch-print', request);
    return response;
  }

  /**
   * Erstellt eine Vorschau-URL für ein Label
   */
  getPreviewUrl(itemId: string, request: LabelPrintRequest): string {
    const params = new URLSearchParams();
    params.append('templateId', request.templateId);
    if (request.includeQR !== undefined) params.append('includeQR', request.includeQR.toString());
    if (request.includeLogo !== undefined) params.append('includeLogo', request.includeLogo.toString());
    if (request.customText) params.append('customText', request.customText);
    
    return `/api/labels/preview/${itemId}?${params.toString()}`;
  }

  /**
   * Validiert Label-Druck-Anfrage
   */
  validatePrintRequest(request: LabelPrintRequest): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!request.templateId) {
      errors.push('Template ID ist erforderlich');
    }

    if (request.copies !== undefined && (request.copies < 1 || request.copies > 100)) {
      errors.push('Kopien müssen zwischen 1 und 100 liegen');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validiert Batch-Druck-Anfrage
   */
  validateBatchPrintRequest(request: BatchPrintRequest): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!request.items || request.items.length === 0) {
      errors.push('Mindestens ein Item ist erforderlich');
    }

    if (request.items && request.items.length > 50) {
      errors.push('Maximal 50 Items pro Batch-Druck erlaubt');
    }

    request.items?.forEach((item, index) => {
      if (!item.itemId) {
        errors.push(`Item ${index + 1}: Item ID ist erforderlich`);
      }
      if (!item.templateId) {
        errors.push(`Item ${index + 1}: Template ID ist erforderlich`);
      }
      if (item.copies !== undefined && (item.copies < 1 || item.copies > 100)) {
        errors.push(`Item ${index + 1}: Kopien müssen zwischen 1 und 100 liegen`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Erstellt Standard-Label-Anfrage basierend auf Item
   */
  createDefaultPrintRequest(item: Item, templateId: string): LabelPrintRequest {
    return {
      templateId,
      includeQR: true,
      includeLogo: false,
      customText: item.isRental ? 'Eigentum der Westfalen AG' : undefined,
      copies: 1
    };
  }

  /**
   * Formatiert Label-Größe für Anzeige
   */
  formatLabelSize(template: LabelTemplate): string {
    return `${template.width}mm × ${template.height}mm`;
  }

  /**
   * Prüft ob Template für Item geeignet ist
   */
  isTemplateSuitable(template: LabelTemplate, item: Item): boolean {
    // Für Mietgeräte: Größere Labels mit Logo-Unterstützung bevorzugen
    if (item.isRental) {
      return template.supportsLogo && template.width >= 18;
    }
    
    // Für normale Items: Alle Templates sind geeignet
    return true;
  }
}

export const labelService = new LabelService();
